import * as d3 from "d3";

const DEFAULT_MARGIN = { top: 18, right: 20, bottom: 52, left: 62 };

export const dashboardSeriesColors = {
  accentBlue: "#5f9fcf",
  accentOrange: "#d48a4f",
  accentRed: "#d36a6a",
  supportGreen: "#63a66f",
};

export const removeTooltip = (tooltipRef) => {
  if (tooltipRef.current) {
    tooltipRef.current.remove();
    tooltipRef.current = null;
  }
};

export const initializeChartCanvas = ({
  svgRef,
  tooltipRef,
  setDimensions,
  margin = DEFAULT_MARGIN,
}) => {
  const svg = d3.select(svgRef.current);
  svg.selectAll("*").remove();
  removeTooltip(tooltipRef);

  const parentContainer = svg.node()?.parentNode;
  if (!parentContainer) return null;

  const parentWidth = parentContainer.clientWidth;
  const parentHeight = parentContainer.clientHeight;
  if (!parentWidth || !parentHeight) return null;

  const innerWidth = Math.max(parentWidth - margin.left - margin.right, 10);
  const innerHeight = Math.max(parentHeight - margin.top - margin.bottom, 10);

  setDimensions({ width: parentWidth, height: parentHeight });

  const chartSvg = svg
    .append("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
    .attr("preserveAspectRatio", "none")
    .append("g")
    .attr("transform", `translate(${margin.left}, ${margin.top})`);

  return {
    chartSvg,
    margin,
    innerWidth,
    innerHeight,
    parentHeight,
    parentWidth,
  };
};

export const createTooltipHandlers = ({
  tooltipRef,
  tooltipClassName = "d3-chart-tooltip",
  showDuration = 120,
  hideDuration = 240,
  offsetX = 10,
  offsetY = 28,
}) => {
  const tooltip = d3
    .select("body")
    .append("div")
    .attr("class", tooltipClassName)
    .style("opacity", 0);

  tooltipRef.current = tooltip;

  const showTooltip = (event, html) => {
    tooltip.transition().duration(showDuration).style("opacity", 1);
    tooltip
      .html(html)
      .style("left", event.pageX + offsetX + "px")
      .style("top", event.pageY - offsetY + "px");
  };

  const hideTooltip = () => {
    tooltip.transition().duration(hideDuration).style("opacity", 0);
  };

  const moveTooltip = (event) => {
    tooltip
      .style("left", event.pageX + offsetX + "px")
      .style("top", event.pageY - offsetY + "px");
  };

  return { hideTooltip, moveTooltip, showTooltip };
};

const tooltipFormatterCache = new Map();

const getTooltipFormatter = (options) => {
  const cacheKey = JSON.stringify(options);
  if (!tooltipFormatterCache.has(cacheKey)) {
    tooltipFormatterCache.set(cacheKey, new Intl.NumberFormat("en-US", options));
  }

  return tooltipFormatterCache.get(cacheKey);
};

export const escapeTooltipHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const formatTooltipNumber = (value, options = {}) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  const formatter = getTooltipFormatter({
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  });

  return formatter.format(numericValue);
};

export const formatTooltipCurrency = (value, maximumFractionDigits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  return formatTooltipNumber(numericValue, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });
};

export const formatTooltipDate = (value, specifier = "%Y-%m-%d") => {
  const dateValue = value instanceof Date ? value : new Date(value);
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return "-";
  }

  return d3.timeFormat(specifier)(dateValue);
};

export const buildTooltipHtml = ({ title, rows = [] }) => {
  const safeRows = rows.filter(
    (row) => row && row.label !== undefined && row.value !== undefined,
  );

  return [
    title ? `<div><strong>${escapeTooltipHtml(title)}</strong></div>` : "",
    ...safeRows.map((row) => {
      const colorDot = row.color
        ? `<span style="display:inline-block;width:8px;height:8px;border-radius:999px;margin-right:6px;background:${row.color};"></span>`
        : "";

      return `<div>${colorDot}<span>${escapeTooltipHtml(row.label)}:</span> <strong>${escapeTooltipHtml(row.value)}</strong></div>`;
    }),
  ].join("");
};

export const bindTooltipEvents = ({
  selection,
  showTooltip,
  hideTooltip,
  htmlAccessor,
}) =>
  selection
    .on("mouseover", (event, datum) => {
      showTooltip(event, htmlAccessor(datum, event));
    })
    .on("mousemove", (event, datum) => {
      showTooltip(event, htmlAccessor(datum, event));
    })
    .on("mouseout", hideTooltip);

export const appendTimeSeriesTooltipOverlay = ({
  chartSvg,
  innerWidth,
  innerHeight,
  xScale,
  getData,
  xAccessor,
  showTooltip,
  hideTooltip,
  htmlAccessor,
}) => {
  const bisector = d3.bisector((datum) => xAccessor(datum)?.valueOf?.() ?? xAccessor(datum)).center;

  const getNearestDatum = (event) => {
    const sourceData = typeof getData === "function" ? getData() : getData;
    const validData = (sourceData ?? []).filter((datum) => {
      const xValue = xAccessor(datum);
      return xValue instanceof Date ? !Number.isNaN(xValue.getTime()) : xValue !== undefined && xValue !== null;
    });

    if (!validData.length) {
      return null;
    }

    const [pointerX] = d3.pointer(event);
    const hoveredValue = xScale.invert(pointerX);
    const nearestIndex = bisector(validData, hoveredValue);
    return validData[nearestIndex] ?? null;
  };

  const overlay = chartSvg
    .append("rect")
    .attr("class", "d3-chart-hover-overlay")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", innerWidth)
    .attr("height", innerHeight)
    .attr("stroke", "none")
    .attr("fill", "#ffffff").attr("fill-opacity", 0.001)
    .style("pointer-events", "all")
    .raise();

  return overlay
    .on("mouseover", (event) => {
      const nearestDatum = getNearestDatum(event);
      if (!nearestDatum) {
        hideTooltip();
        return;
      }

      showTooltip(event, htmlAccessor(nearestDatum, event));
    })
    .on("mousemove", (event) => {
      const nearestDatum = getNearestDatum(event);
      if (!nearestDatum) {
        hideTooltip();
        return;
      }

      showTooltip(event, htmlAccessor(nearestDatum, event));
    })
    .on("mouseout", hideTooltip);
};

export const appendBandTooltipTargets = ({
  chartSvg,
  data,
  xScale,
  innerHeight,
  xAccessor,
  showTooltip,
  hideTooltip,
  htmlAccessor,
}) =>
  chartSvg
    .append("g")
    .attr("class", "d3-chart-hover-overlay-group")
    .selectAll("rect")
    .data(data)
    .enter()
    .append("rect")
    .attr("class", "d3-chart-hover-overlay")
    .attr("x", (datum) => xScale(xAccessor(datum)))
    .attr("y", 0)
    .attr("width", xScale.bandwidth())
    .attr("height", innerHeight)
    .attr("stroke", "none")
    .attr("fill", "#ffffff").attr("fill-opacity", 0.001)
    .style("pointer-events", "all")
    .on("mouseover", (event, datum) => {
      showTooltip(event, htmlAccessor(datum, event));
    })
    .on("mousemove", (event, datum) => {
      showTooltip(event, htmlAccessor(datum, event));
    })
    .on("mouseout", hideTooltip);
export const drawTimePriceGrid = ({
  chartSvg,
  xScale,
  yScale,
  tickValues,
  maxYTicks,
  innerWidth,
  innerHeight,
}) => {
  const xGrid = d3
    .axisBottom(xScale)
    .tickValues(tickValues)
    .tickSize(-innerHeight)
    .tickFormat(() => "");

  const yGrid = d3
    .axisLeft(yScale)
    .ticks(maxYTicks)
    .tickSize(-innerWidth)
    .tickFormat(() => "");

  chartSvg
    .append("g")
    .attr("class", "d3-chart-gridline")
    .attr("transform", `translate(0, ${innerHeight})`)
    .call(xGrid);

  chartSvg.append("g").attr("class", "d3-chart-gridline").call(yGrid);
};

export const appendVerticalAreaGradient = ({
  chartSvg,
  idPrefix,
  color,
  stops,
}) => {
  const defs = chartSvg.append("defs");
  const gradientId = `${idPrefix}-${Math.random().toString(36).slice(2)}`;

  const gradient = defs
    .append("linearGradient")
    .attr("id", gradientId)
    .attr("x1", "0%")
    .attr("y1", "0%")
    .attr("x2", "0%")
    .attr("y2", "100%");

  stops.forEach(({ offset, opacity }) => {
    gradient
      .append("stop")
      .attr("offset", offset)
      .attr("stop-color", color)
      .attr("stop-opacity", opacity);
  });

  return gradientId;
};

export const buildPeriodAverageSeries = ({
  rows = [],
  dateAccessor = (row) => row.chartDate,
  valueAccessor = (row) => row.price,
  valueKey = "price",
}) => {
  const groupedByPeriod = new Map();

  rows.forEach((row) => {
    const rawDate = dateAccessor(row);
    const rawValue = valueAccessor(row);
    const date = rawDate instanceof Date ? rawDate : new Date(rawDate);

    if (rawValue === null || rawValue === undefined || rawValue === "") return;

    const value = Number(rawValue);

    if (Number.isNaN(date.getTime()) || !Number.isFinite(value)) return;

    const periodKey = date.getTime();
    const group = groupedByPeriod.get(periodKey) ?? {
      firstRow: row,
      sum: 0,
      count: 0,
    };

    group.sum += value;
    group.count += 1;
    groupedByPeriod.set(periodKey, group);
  });

  return Array.from(groupedByPeriod.entries())
    .map(([periodKey, group]) => ({
      ...group.firstRow,
      chartDate: new Date(Number(periodKey)),
      [valueKey]: group.sum / group.count,
      periodValueCount: group.count,
    }))
    .sort((a, b) => a.chartDate - b.chartDate);
};

export const appendSignedVerticalAreaGradients = ({
  chartSvg,
  idPrefix,
  color,
  positiveStops = [
    { offset: "0%", opacity: 0.24 },
    { offset: "34%", opacity: 0.09 },
    { offset: "100%", opacity: 0 },
  ],
  negativeStops = [
    { offset: "0%", opacity: 0 },
    { offset: "66%", opacity: 0.09 },
    { offset: "100%", opacity: 0.24 },
  ],
}) => {
  const defs = chartSvg.append("defs");
  const uniqueId = Math.random().toString(36).slice(2);
  const positiveGradientId = `${idPrefix}-positive-${uniqueId}`;
  const negativeGradientId = `${idPrefix}-negative-${uniqueId}`;

  const positiveGradient = defs
    .append("linearGradient")
    .attr("id", positiveGradientId)
    .attr("x1", "0%")
    .attr("y1", "0%")
    .attr("x2", "0%")
    .attr("y2", "100%");

  positiveStops.forEach(({ offset, opacity }) => {
    positiveGradient
      .append("stop")
      .attr("offset", offset)
      .attr("stop-color", color)
      .attr("stop-opacity", opacity);
  });

  const negativeGradient = defs
    .append("linearGradient")
    .attr("id", negativeGradientId)
    .attr("x1", "0%")
    .attr("y1", "0%")
    .attr("x2", "0%")
    .attr("y2", "100%");

  negativeStops.forEach(({ offset, opacity }) => {
    negativeGradient
      .append("stop")
      .attr("offset", offset)
      .attr("stop-color", color)
      .attr("stop-opacity", opacity);
  });

  return { negativeGradientId, positiveGradientId };
};

export const getNumericExtentIncludingZero = (
  values,
  fallback = [0, 1],
) => {
  const finiteValues = values.filter((value) => Number.isFinite(value));

  if (!finiteValues.length) {
    return fallback;
  }

  const min = Math.min(0, d3.min(finiteValues));
  const max = Math.max(0, d3.max(finiteValues));

  if (min === max) {
    if (min === 0) {
      return fallback;
    }

    return [Math.min(0, min), Math.max(0, max + 1)];
  }

  return [min, max];
};

export const createZeroAnchoredArea = ({
  curve = d3.curveMonotoneX,
  polarity = "positive",
  valueAccessor,
  xAccessor,
  xScale,
  yScale,
}) => {
  const isPositive = polarity === "positive";

  return d3
    .area()
    .defined((datum) => {
      const value = Number(valueAccessor(datum));
      return Number.isFinite(value) && (isPositive ? value >= 0 : value <= 0);
    })
    .x((datum) => xScale(xAccessor(datum)))
    .y0(() => yScale(0))
    .y1((datum) => {
      const value = Number(valueAccessor(datum));
      return yScale(isPositive ? Math.max(value, 0) : Math.min(value, 0));
    })
    .curve(curve);
};

export const appendCircleSeries = ({
  chartSvg,
  selector = "circle",
  rows,
  className,
  yAccessor,
  radius,
  tooltipAccessor,
  xScale,
  yScale,
  showTooltip,
  moveTooltip,
  hideTooltip,
}) => {
  chartSvg
    .append("g")
    .selectAll(selector)
    .data(rows)
    .enter()
    .append("circle")
    .attr("class", className)
    .attr("cx", (d) => xScale(d.chartDate))
    .attr("cy", (d) => yScale(yAccessor(d)))
    .attr("r", radius)
    .on("mouseover", function (event, d) {
      showTooltip(event, tooltipAccessor(d));
    })
    .on("mousemove", moveTooltip)
    .on("mouseout", hideTooltip);
};

export const drawTimePriceAxes = ({
  chartSvg,
  xScale,
  yScale,
  tickValues,
  tickFormat,
  maxYTicks,
  currencySymbol,
  pricePrecision,
  innerHeight,
}) => {
  const priceFormat =
    (currencySymbol ?? "$") + ",." + (pricePrecision ?? "2") + "f";

  const xAxis = d3
    .axisBottom(xScale)
    .tickValues(tickValues)
    .tickFormat(d3.timeFormat(tickFormat));

  const yAxis = d3
    .axisLeft(yScale)
    .ticks(maxYTicks)
    .tickFormat(d3.format(priceFormat));

  chartSvg
    .append("g")
    .attr("class", "d3-chart-axis")
    .attr("transform", `translate(0, ${innerHeight})`)
    .call(xAxis)
    .selectAll("text")
    .style("text-anchor", "end")
    .attr("transform", "rotate(-35)")
    .attr("dx", "-0.5em")
    .attr("dy", "0.15em");

  chartSvg
    .append("g")
    .attr("class", "d3-chart-axis")
    .call(yAxis)
    .selectAll("text")
    .attr("dy", "-0.3em")
    .style("text-anchor", "end");
};






